<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useTranslation } from "i18next-vue";
import DraggerMenuState, {
  LINEWEIGHT_DISPLAY_MULTIPLIERS,
} from "@/stores/DraggerMenuState";
import CanvasBusyOverlay from "@/components/canvas/CanvasBusyOverlay.vue";

/**
 * Épaisseur des traits à l'écran : cran 0 masque les épaisseurs, les crans
 * suivants les grossissent pour l'œil (écrans à pixels très fins) sans
 * toucher aux épaisseurs AutoCAD du dessin.
 */
const { t } = useTranslation();
const isLoading = ref(false);
const loadingStatus = ref("");

// Valeur locale : le curseur bouge librement, le réglage s'applique au lâcher.
const level = ref(DraggerMenuState.lineweightLevel);
watch(
  () => DraggerMenuState.lineweightLevel,
  (value) => (level.value = value),
);

const maxLevel = LINEWEIGHT_DISPLAY_MULTIPLIERS.length - 1;

const valueLabel = computed(() =>
  level.value === 0
    ? t("canvas:settings.lineWeight.hidden")
    : `×${LINEWEIGHT_DISPLAY_MULTIPLIERS[level.value]}`,
);

async function applyLevel(value: number) {
  if (value === DraggerMenuState.lineweightLevel) return;
  // Afficher/masquer les épaisseurs régénère tout le dessin : on prévient.
  const togglesDisplay = (value > 0) !== DraggerMenuState.isLwActive;
  if (togglesDisplay) {
    isLoading.value = true;
    loadingStatus.value = t(
      value > 0
        ? "canvas:settings.lineWeight.activating"
        : "canvas:settings.lineWeight.deactivating",
    );
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  DraggerMenuState.setLineweightLevel(value);
  isLoading.value = false;
}
</script>

<template>
  <!-- Mêmes marges qu'une ligne option-btn, sans son survol cliquable -->
  <div class="rounded px-3 py-2 w-100 d-flex flex-column gap-1 text-white">
    <div class="d-flex align-items-center gap-2">
      <div class="col d-flex flex-column text-start">
        <div class="text-truncate text-white">
          {{ $t("canvas:settings.lineWeight.option.type.title") }}
        </div>
        <div class="text-grey" style="line-height: 15px !important">
          <small>{{ $t("canvas:settings.lineWeight.option.type.desc") }}</small>
        </div>
      </div>
      <span class="lineweight-value text-white">{{ valueLabel }}</span>
    </div>
    <el-slider
      v-model="level"
      :min="0"
      :max="maxLevel"
      :step="1"
      show-stops
      :show-tooltip="false"
      class="lineweight-slider"
      :aria-label="$t('canvas:settings.lineWeight.option.type.title')"
      @change="(value) => applyLevel(value as number)"
    />
  </div>
  <!-- Fullscreen loading overlay - direct render, no teleport -->
  <CanvasBusyOverlay v-if="isLoading" :status="loadingStatus" />
</template>

<style lang="scss" scoped>
.lineweight-value {
  flex-shrink: 0;
  min-width: 2.5em;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.lineweight-slider {
  --el-slider-main-bg-color: var(--canvas-btn-active-bg);
  padding: 0 8px;
}
</style>
